import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { getAuthToken } from '../store/auth.store'
import { tenantService } from './tenant.service'
import { API_CONFIG } from '../config/api.config'
import { shouldRetry, getRetryDelay } from '../utils/api-retry'
import { cacheReadThrough, serveOfflineRead, queueOfflineWrite, isWriteMethod } from './offline-sync'

// API Configuration
const API_BASE_URL = API_CONFIG.BASE_URL
const API_TIMEOUT = API_CONFIG.TIMEOUT

// Report/analytics endpoints scan the full visit history; they get a longer budget
// than an interactive call so a slow-but-progressing request finishes instead of
// being aborted and retried (the abort does not stop the Worker, so a retry only
// adds load and makes the next attempt slower).
function timeoutForUrl(url?: string): number {
  if (!url) return API_TIMEOUT
  return API_CONFIG.SLOW_PATH_PREFIXES.some(p => url.includes(p))
    ? API_CONFIG.REPORT_TIMEOUT
    : API_TIMEOUT
}

// Lightweight in-memory cache for GET requests (avoids refetching on tab navigation)
const responseCache = new Map<string, { data: AxiosResponse; timestamp: number }>()
const pendingRequests = new Map<string, Promise<AxiosResponse>>() // Request deduplication
const CACHE_TTL_MS = 120_000 // 2 minutes (was 30s - optimized for mobile)
const STALE_TTL_MS = 300_000 // 5 minutes stale-while-revalidate for mobile
const CACHEABLE_PATHS = ['/agent/dashboard', '/agent/performance', '/team-lead/dashboard', '/manager/dashboard', '/mobile/']

export function invalidateApiCache(pathPrefix?: string) {
  if (pathPrefix) {
    for (const key of responseCache.keys()) {
      if (key.startsWith(pathPrefix)) responseCache.delete(key)
    }
  } else {
    responseCache.clear()
  }
}

// Create axios instance with baseURL
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

function getValidToken(): string | undefined {
  // Try to get token from Zustand store first
  const fromStore = getAuthToken()
  if (typeof fromStore === 'string' && fromStore && fromStore !== 'null' && fromStore !== 'undefined') {
    return fromStore
  }
  
  if (typeof window !== 'undefined') {
    try {
      const persisted = localStorage.getItem('fieldvibe-auth')
      if (persisted) {
        const parsed = JSON.parse(persisted)
        const token = parsed?.state?.tokens?.access_token
        if (typeof token === 'string' && token && token !== 'null' && token !== 'undefined') {
          return token
        }
      }
    } catch (error) {
      console.error('Failed to read token from localStorage:', error)
    }
  }
  
  return undefined
}

// Request interceptor to add auth token and tenant header
apiClient.interceptors.request.use(
  (config) => {
    const token = getValidToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      delete config.headers.Authorization
    }
    // Add dynamic tenant header for multi-tenant support
    const tenantCode = tenantService.getTenantCode()
    config.headers['X-Tenant-Code'] = tenantCode
    // Report/analytics paths get the longer budget unless the caller set one.
    if (config.timeout === undefined || config.timeout === API_TIMEOUT) {
      config.timeout = timeoutForUrl(config.url)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Stale-while-revalidate cache + request deduplication for mobile dashboard endpoints
// Returns stale data instantly while refreshing in background; fresh within TTL returns immediately
// Deduplicates concurrent identical requests to avoid redundant network calls
const originalGet = apiClient.get.bind(apiClient)
apiClient.get = function cachedGet(url: string, config?: AxiosRequestConfig) {
  const isCacheable = CACHEABLE_PATHS.some(p => url.includes(p))
  if (isCacheable) {
    const cached = responseCache.get(url)
    if (cached) {
      const age = Date.now() - cached.timestamp
      if (age < CACHE_TTL_MS) {
        // Fresh — return immediately, no revalidation needed
        return Promise.resolve(cached.data)
      }
      if (age < STALE_TTL_MS) {
        // Stale — return cached data immediately, revalidate in background
        // Mark as background so interceptor skips logout/redirect on 401/403
        originalGet(url, { ...config, _backgroundRevalidation: true } as any).then((res: AxiosResponse) => {
          responseCache.set(url, { data: res, timestamp: Date.now() })
        }).catch(() => { /* background refresh failed, keep stale */ })
        return Promise.resolve(cached.data)
      }
    }
    // Check for pending request (deduplication)
    const pending = pendingRequests.get(url)
    if (pending) {
      return pending
    }
  }
  // Create new request with deduplication
  const requestPromise = originalGet(url, config).then((res: AxiosResponse) => {
    if (isCacheable) {
      responseCache.set(url, { data: res, timestamp: Date.now() })
    }
    pendingRequests.delete(url) // Clean up pending request
    return res
  }).catch((err) => {
    pendingRequests.delete(url) // Clean up on error too
    throw err
  })
  
  if (isCacheable) {
    pendingRequests.set(url, requestPromise)
  }
  return requestPromise
} as typeof apiClient.get

// Response interceptor for error handling with retry logic
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Write successful GETs through to IndexedDB so they're readable offline later.
    cacheReadThrough(response)
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any

    // Skip auth error handling for background SWR revalidation requests
    if (originalRequest._backgroundRevalidation) {
      return Promise.reject(error)
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Try to refresh token
        const { useAuthStore } = await import('../store/auth.store')
        await useAuthStore.getState().refreshToken()
        
        // Retry original request with new token
        const token = getAuthToken()
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        // The store logs out only when /auth/refresh definitively rejected
        // the token (401/403). A 429 (shared CGNAT IP rate limit), network
        // blip or 5xx keeps the session — hard-redirecting on those flashed
        // whole field teams to the login screen mid-shift.
        const { useAuthStore } = await import('../store/auth.store')
        if (!useAuthStore.getState().isAuthenticated && typeof window !== 'undefined'
            && !window.location.pathname.startsWith('/auth/')) {
          window.location.href = '/auth/login'
        }
      }
    }

    // 403 Forbidden — the user IS authenticated, they just lack permission for
    // this one endpoint. Do NOT redirect to login: the login page bounces
    // authenticated users straight back, so any role-gated background query
    // (e.g. manager hitting /issues/unmanaged) turned into an app-wide
    // redirect/flash loop. Let the caller handle the rejection.
    if (error.response?.status === 403) {
      console.error('Access Forbidden: Insufficient permissions')
    }

    const method = originalRequest?.method?.toUpperCase()
    const isIdempotent = method === 'GET' || method === 'HEAD' || method === 'OPTIONS'
    
    if (isIdempotent && shouldRetry(error) && !originalRequest._retryCount) {
      originalRequest._retryCount = 0
    }

    // A timeout means the server was still working, not that the request was lost.
    // Retrying it stacks another identical query on top of the one still running,
    // so allow a single retry rather than three.
    const isTimeout = !error.response && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT')
    const maxRetries = isTimeout ? 1 : 3

    if (isIdempotent && originalRequest._retryCount !== undefined && originalRequest._retryCount < maxRetries) {
      originalRequest._retryCount++
      const delay = getRetryDelay(originalRequest._retryCount)
      
      
      await new Promise(resolve => setTimeout(resolve, delay))
      return apiClient(originalRequest)
    }

    // Handle network errors — offline fallback before giving up.
    if (!error.response) {
      // A write we can't send now: queue it for replay on reconnect, resolve optimistically.
      if (isWriteMethod(originalRequest?.method)) {
        try {
          return await queueOfflineWrite(originalRequest)
        } catch {
          /* queue unavailable — fall through to the error below */
        }
      } else {
        // A read: serve the last cached payload if we have one.
        const cached = await serveOfflineRead(originalRequest)
        if (cached) return cached
      }
      console.error('Network Error: Unable to reach the server')
      return Promise.reject({
        message: 'Network error: Unable to connect to the server. Please check your internet connection.',
        code: 'NETWORK_ERROR',
        status: 0,
      })
    }

    // Handle other errors
    const errorData = error.response?.data as Record<string, any> | undefined
    const errorMessage = errorData?.message || error.message || 'An error occurred'
    const errorCode = errorData?.code || 'UNKNOWN_ERROR'
    
    return Promise.reject({
      message: errorMessage,
      code: errorCode,
      status: error.response?.status,
      data: error.response?.data,
    })
  }
)

// Generic API service class
export class ApiService {
  protected client: AxiosInstance

  constructor() {
    this.client = apiClient
  }

  // GET request
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config)
  }

  // POST request
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config)
  }

  // PUT request
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config)
  }

  // PATCH request
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config)
  }

  // DELETE request
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config)
  }

  // Upload file
  async upload<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<AxiosResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    return this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
  }

  // Download file
  async download(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    })

    // Create download link
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }
}

// Export default instance
export const apiService = new ApiService()

// Utility functions
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })
  
  return searchParams.toString()
}

export const buildUrl = (baseUrl: string, params?: Record<string, any>): string => {
  if (!params) return baseUrl
  
  const queryString = buildQueryString(params)
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}

// Error handling utilities
export const isApiError = (error: any): boolean => {
  return error && typeof error === 'object' && 'message' in error
}

export const getErrorMessage = (error: any): string => {
  if (isApiError(error)) {
    return error.message
  }
  
  if (error instanceof Error) {
    return error.message
  }
  
  return 'An unexpected error occurred'
}

export const getErrorCode = (error: any): string => {
  if (isApiError(error) && error.code) {
    return error.code
  }
  
  return 'UNKNOWN_ERROR'
}

// Request/Response logging (development only)
if (import.meta.env.DEV) {
  apiClient.interceptors.request.use((config) => {
    return config
  })

  apiClient.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      console.error(`API Error: ${error.response?.status} ${error.config?.url}`, {
        error: error.response?.data || error.message,
      })
      return Promise.reject(error)
    }
  )
}
